/**
 * English fallbacks for message ids this front-end introduced.
 *
 * Translations are fetched from the platform, so an id the backend has not shipped yet
 * renders as its own id — "filter__sheet_apply" in the middle of a button. These are
 * merged *under* the fetched translations in LanguageProvider, so the platform always
 * wins and this only ever fills a hole.
 *
 * The rule that goes with this file: a new id gets an entry here in the same commit
 * that introduces it. An id that already exists in the platform's translations does not
 * belong here — reuse the existing one instead of adding a synonym.
 */
export const DEFAULT_MESSAGES: Record<string, string> = {
  // Home
  home__live_now: "{count} live now",

  // Filter sheets
  filter__sheet_apply: "Apply",
  filter__sheet_clear: "Clear",
  filter__sheet_close: "Close without changing anything",
  filter__sheet_back: "Back to more filters",
  filter__selected_count: "{count} selected",
  filter__more_title: "More filters",
  filter__more_subtitle:
    "{count, plural, =0 {Thermometer and gender stay on the feed} one {# on · thermometer and gender stay on the feed} other {# on · thermometer and gender stay on the feed}}",
  filter__search_language: "Search a language",
  filter__subtitle_thermometers: "Several can be on at once",
  filter__subtitle_genders: "Several can be on at once",
  filter__subtitle_age_ranges: "Several can be on at once",
  filter__subtitle_languages: "Several can be on at once",
  filter__subtitle_website_uses: "What members say they're here for",
  filter__subtitle_relationship_sought: "What members are looking for",
  filter__subtitle_has_fanclub: "Paid content behind a subscription",
  filter__any_thermometer: "Any heat",
  filter__any_gender: "Anyone",
  filter__any_age: "Any age",
  filter__any_language: "Any language",
  filter__any_website_use: "Any use of the site",
  filter__any_relationship: "Any relationship",
  filter__any_fanclub: "Free and paid",
  filter__explain_thermometer_soft: "Suggestive, nothing shown",
  filter__explain_thermometer_hot: "Partial nudity",
  filter__explain_thermometer_hard: "Nothing held back · needs a verified account",
  filter__explain_fanclub_yes: "Members who sell access to their snaps",
  filter__explain_fanclub_no: "Everything they post is free",

  // Appearance and vibe
  parameters__appearance: "Appearance",
  appearance__label: "Appearance",
  appearance__light: "Light",
  appearance__dark: "Dark",
  appearance__system: "System",
  vibe__label: "Vibe",
  vibe__sharp: "Sharp",
  vibe__default: "Default",
  vibe__soft: "Soft",
  vibe__explain: "How rounded everything is, across the whole site.",

  // Profile blocks
  profile__online_now: "Online now",
  profile__content: "Content",
  profile__album: "Album",
  profile__album_empty: "Nothing here yet.",
  profile__fanclub_only: "Fan club only",
  profile__fanclub_member: "You're a member",
  profile__fanclub_pitch: "Permanent photos and videos, kept for members of the club.",
  profile__fanclub_flames: "Flames needed",
  profile__fanclub_messages: "Messages needed",
  profile__fanclub_how: "How to join",
  profile__arrange: "Arrange my profile",
  profile__arrange_done: "Done",
  profile__arrange_hint:
    "Reorder your blocks and choose which ones visitors see. Identity always comes first.",
  profile__arrange_more: "More blocks are on the way",
  profile__block_pinned: "Always shown, always first",
  profile__block_identity: "Identity",
  profile__block_identity_hint: "Cover, avatar, name, bio",
  profile__block_highlights: "Highlights",
  profile__block_highlights_hint: "The sections of your profile",
  profile__block_fanclub_hint: "Your club and how to join it",
  profile__block_about_hint: "What you're looking for",
  profile__block_content_hint: "Snaps, album and fan club",

  // Gift sheet (26). The flow's own strings reuse the chat tray's platform ids
  // (gifttray__header_common_boost, giftmessage__*); these fill only what the
  // profile entry point adds.
  gifttray__title: "Send a gift",
  gifttray__subtitle: "To {member}",
  gifttray__balance: "{count, number} tokens",
  gifttray__cost_after: "{cost, number} tokens · {left, number} left after",
  gifttray__back: "Back",
  gifttray__done: "Done",
  gifttray__sent_summary: "+{flames, number} flames for {member} · {left, number} tokens left.",
  gifttray__sent_rank: "Now #{rank, number}.",
  gifttray__sent_message: "You sent a gift"
};

export default DEFAULT_MESSAGES;
